import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

gsap?.registerPlugin(ScrollTrigger);

const FAQSection = () => {
  const [language, setLanguage] = useState('en');
  const [searchTerm, setSearchTerm] = useState('');
  const [openFAQ, setOpenFAQ] = useState(null);
  const [filteredFAQs, setFilteredFAQs] = useState([]);
  const sectionRef = useRef(null);
  const faqsRef = useRef([]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    const ctx = gsap?.context(() => {
      faqsRef?.current?.forEach((faq, index) => {
        if (faq) {
          gsap?.fromTo(
            faq,
            { y: 30, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.6,
              delay: index * 0.1,
              scrollTrigger: {
                trigger: faq,
                start: 'top 85%',
                toggleActions: 'play none none reverse',
              },
            }
          );
        }
      });
    }, sectionRef);

    return () => ctx?.revert();
  }, [filteredFAQs]);

  const content = {
    en: {
      title: 'Frequently Asked Questions',
      subtitle: 'Find answers to common questions about Slotyfi',
      searchPlaceholder: 'Search questions...',
      noResults: 'No questions found matching your search.',
      faqs: [
        {
          id: 1,
          category: 'Getting Started',
          question: 'How quickly can I set up Slotyfi for my business?',
          answer: `Setup takes just 15-30 minutes! Our onboarding process includes:\n\n• Account creation and business profile setup\n• Integration with your existing calendar (Google, Outlook, etc.)\n• Channel connections (WhatsApp, Telegram, Instagram)\n• Staff member addition and permission settings\n• Custom booking flow configuration\n\nOur support team provides live assistance during setup to ensure everything works perfectly from day one.`,
          tags: ['setup', 'onboarding', 'integration', 'getting started'],
        },
        {
          id: 2,
          category: 'Integrations',
          question:
            'Which social media platforms and tools does Slotyfi integrate with?',
          answer: `Slotyfi integrates with all major platforms:\n\n**Social Media:**\n• WhatsApp Business API\n• Telegram Bot API\n• Instagram Direct Messages\n• Facebook Messenger (coming soon)\n\n**Calendar Systems:**\n• Google Calendar\n• Outlook Calendar\n• Apple Calendar\n• Custom calendar APIs\n\n**Payment Processors:**\n• Stripe\n• PayPal\n• Square\n• Local payment gateways\n\n**Other Tools:**\n• Zoom for virtual appointments\n• SMS providers\n• Email marketing platforms`,
          tags: [
            'integrations',
            'social media',
            'calendar',
            'payments',
            'whatsapp',
            'telegram',
            'instagram',
          ],
        },
        {
          id: 3,
          category: 'Pricing & Plans',
          question: 'Can I change my plan anytime? What about refunds?',
          answer: `Yes, you have complete flexibility:\n\n**Plan Changes:**\n• Upgrade anytime - changes take effect immediately\n• Downgrade at the end of your current billing cycle\n• No setup fees for plan changes\n• Prorated billing for upgrades\n\n**Refund Policy:**\n• 30-day money-back guarantee for new customers\n• Cancel anytime with no penalties\n• Unused portion refunded for annual plans\n• No questions asked refund process\n\n**Free Trial:**\n• 14-day free trial on all plans\n• No credit card required to start\n• Full feature access during trial`,
          tags: ['pricing', 'plans', 'refunds', 'trial', 'billing', 'cancel'],
        },
        {
          id: 4,
          category: 'Features',
          question: 'How does the AI booking assistant work?',
          answer: `Our AI assistant uses advanced natural language processing:\n\n**Capabilities:**\n• Understands context and intent in multiple languages\n• Handles complex scheduling scenarios\n• Learns from your business patterns\n• Provides human-like conversational experience\n\n**Smart Features:**\n• Automatic conflict detection and resolution\n• Intelligent time slot suggestions\n• Customer preference learning\n• Upselling and cross-selling recommendations\n\n**Customization:**\n• Train AI with your specific business terminology\n• Custom response templates\n• Brand voice and tone matching\n• Industry-specific conversation flows`,
          tags: [
            'ai',
            'assistant',
            'automation',
            'natural language',
            'smart booking',
          ],
        },
        {
          id: 5,
          category: 'Security',
          question: 'How secure is my customer data with Slotyfi?',
          answer: `Security is our top priority:\n\n**Data Protection:**\n• End-to-end encryption for all communications\n• GDPR and CCPA compliant\n• SOC 2 Type II certified\n• Regular security audits and penetration testing\n\n**Infrastructure:**\n• AWS cloud hosting with 99.9% uptime\n• Automated backups every 6 hours\n• Disaster recovery protocols\n• Geographic data redundancy\n\n**Access Control:**\n• Multi-factor authentication\n• Role-based permissions\n• Audit logs for all activities\n• Secure API endpoints\n\n**Compliance:**\n• HIPAA compliant for healthcare businesses\n• PCI DSS compliant for payment processing`,
          tags: [
            'security',
            'data protection',
            'encryption',
            'compliance',
            'gdpr',
            'hipaa',
          ],
        },
        {
          id: 6,
          category: 'Support',
          question: 'What kind of support do you provide?',
          answer: `We offer comprehensive support across all plans:\n\n**Support Channels:**\n• Live chat (24/7 for Advanced+ plans)\n• Email support with <4 hour response time\n• Phone support for Standard+ plans\n• Video call assistance for complex issues\n\n**Resources:**\n• Comprehensive knowledge base\n• Video tutorials and webinars\n• Best practices guides\n• Community forum\n\n**Onboarding:**\n• Dedicated onboarding specialist\n• Live setup assistance\n• Staff training sessions\n• Custom workflow design\n\n**Ongoing Support:**\n• Regular check-ins for optimization\n• Feature update training\n• Performance monitoring and alerts`,
          tags: ['support', 'help', 'training', 'onboarding', 'assistance'],
        },
        {
          id: 7,
          category: 'Technical',
          question: 'Do I need technical knowledge to use Slotyfi?',
          answer: `Not at all! Slotyfi is designed for non-technical users:\n\n**User-Friendly Design:**\n• Intuitive dashboard with drag-and-drop interface\n• Step-by-step setup wizards\n• Visual workflow builders\n• No coding required\n\n**Easy Integration:**\n• One-click social media connections\n• Automatic calendar syncing\n• Pre-built templates for common scenarios\n• Smart defaults that work out of the box\n\n**Support Available:**\n• Live setup assistance\n• Screen sharing for complex configurations\n• Video tutorials for every feature\n• Dedicated customer success manager\n\n**Advanced Options:**\n• API access for technical users\n• Custom integrations available\n• Webhook support for developers`,
          tags: ['technical', 'easy', 'user-friendly', 'no coding', 'setup'],
        },
        {
          id: 8,
          category: 'Business Impact',
          question: 'What results can I expect in the first month?',
          answer: `Most businesses see immediate improvements:\n\n**Week 1:**\n• 50-70% reduction in booking-related phone calls\n• Automated responses to common questions\n• Basic appointment scheduling working\n\n**Week 2-3:**\n• 60-80% reduction in no-shows (with reminders active)\n• Staff reporting 40-60% time savings\n• Customer satisfaction improvements\n\n**Month 1 Results:**\n• 85%+ no-show reduction (industry average)\n• 15-25 hours/week staff time savings\n• 20-35% increase in booking conversion\n• 95%+ customer satisfaction with booking process\n\n**Long-term Benefits:**\n• 30-50% revenue increase from reduced no-shows\n• Improved staff morale and productivity\n• Better customer retention and referrals\n• Scalable growth without proportional staff increases`,
          tags: [
            'results',
            'roi',
            'benefits',
            'timeline',
            'improvements',
            'no-shows',
          ],
        },
      ],
    },
    fa: {
      title: 'سوالات متداول',
      subtitle: 'پاسخ سوالات رایج درباره اسلاتیفای را پیدا کنید',
      searchPlaceholder: 'جستجوی سوالات...',
      noResults: 'هیچ سوالی مطابق با جستجوی شما یافت نشد.',
      faqs: [
        {
          id: 1,
          category: 'شروع کار',
          question:
            'چقدر سریع می‌توانم اسلاتیفای را برای کسب‌وکارم راه‌اندازی کنم؟',
          answer: `راه‌اندازی فقط 15-30 دقیقه طول می‌کشد! فرآیند آموزش ما شامل:\n\n• ایجاد حساب و تنظیم پروفایل کسب‌وکار\n• یکپارچگی با تقویم موجود شما (گوگل، اوت‌لوک و غیره)\n• اتصال کانال‌ها (واتساپ، تلگرام، اینستاگرام)\n• اضافه کردن اعضای کارکنان و تنظیمات مجوز\n• پیکربندی جریان رزرو سفارشی\n\nتیم پشتیبانی ما کمک زنده در طول راه‌اندازی ارائه می‌دهد تا اطمینان حاصل کند همه چیز از روز اول کاملاً کار می‌کند.`,
          tags: ['راه‌اندازی', 'آموزش', 'یکپارچگی', 'شروع کار'],
        },
        {
          id: 2,
          category: 'یکپارچگی‌ها',
          question:
            'اسلاتیفای با کدام پلتفرم‌های شبکه‌های اجتماعی و ابزارها یکپارچه می‌شود؟',
          answer: `اسلاتیفای با همه پلتفرم‌های اصلی یکپارچه می‌شود:\n\n**شبکه‌های اجتماعی:**\n• API واتساپ بیزنس\n• API ربات تلگرام\n• پیام‌های مستقیم اینستاگرام\n• فیسبوک مسنجر (به زودی)\n\n**سیستم‌های تقویم:**\n• تقویم گوگل\n• تقویم اوت‌لوک\n• تقویم اپل\n• APIهای تقویم سفارشی\n\n**پردازشگرهای پرداخت:**\n• استرایپ\n• پی‌پال\n• اسکوئر\n• درگاه‌های پرداخت محلی\n\n**ابزارهای دیگر:**\n• زوم برای قرارملاقات‌های مجازی\n• ارائه‌دهندگان پیامک\n• پلتفرم‌های بازاریابی ایمیل`,
          tags: [
            'یکپارچگی‌ها',
            'شبکه‌های اجتماعی',
            'تقویم',
            'پرداخت‌ها',
            'واتساپ',
            'تلگرام',
            'اینستاگرام',
          ],
        },
        {
          id: 3,
          category: 'قیمت‌گذاری و طرح‌ها',
          question:
            'آیا می‌توانم هر زمان طرحم را تغییر دهم؟ در مورد بازپرداخت چطور؟',
          answer: `بله، شما انعطاف کامل دارید:\n\n**تغییرات طرح:**\n• هر زمان ارتقا دهید - تغییرات فوراً اعمال می‌شود\n• در پایان چرخه صورتحساب فعلی کاهش دهید\n• بدون هزینه راه‌اندازی برای تغییرات طرح\n• صورتحساب تناسبی برای ارتقاها\n\n**سیاست بازپرداخت:**\n• ضمانت بازگشت پول 30 روزه برای مشتریان جدید\n• هر زمان بدون جریمه لغو کنید\n• بخش استفاده نشده برای طرح‌های سالانه بازپرداخت می‌شود\n• فرآیند بازپرداخت بدون سوال\n\n**آزمایش رایگان:**\n• آزمایش رایگان 14 روزه در همه طرح‌ها\n• برای شروع نیازی به کارت اعتباری نیست\n• دسترسی کامل به ویژگی‌ها در طول آزمایش`,
          tags: [
            'قیمت‌گذاری',
            'طرح‌ها',
            'بازپرداخت',
            'آزمایش',
            'صورتحساب',
            'لغو',
          ],
        },
        {
          id: 4,
          category: 'ویژگی‌ها',
          question: 'دستیار رزرو هوش مصنوعی چگونه کار می‌کند؟',
          answer: `دستیار هوش مصنوعی ما از پردازش زبان طبیعی پیشرفته استفاده می‌کند:\n\n**قابلیت‌ها:**\n• درک زمینه و قصد در زبان‌های متعدد\n• مدیریت سناریوهای پیچیده برنامه‌ریزی\n• یادگیری از الگوهای کسب‌وکار شما\n• ارائه تجربه گفتگوی انسان‌مانند\n\n**ویژگی‌های هوشمند:**\n• تشخیص و حل خودکار تضاد\n• پیشنهادات هوشمند زمان‌بندی\n• یادگیری ترجیحات مشتری\n• توصیه‌های فروش متقابل و افزایشی\n\n**شخصی‌سازی:**\n• آموزش هوش مصنوعی با اصطلاحات خاص کسب‌وکار شما\n• قالب‌های پاسخ سفارشی\n• تطبیق صدا و لحن برند\n• جریان‌های گفتگوی خاص صنعت`,
          tags: [
            'هوش مصنوعی',
            'دستیار',
            'خودکارسازی',
            'زبان طبیعی',
            'رزرو هوشمند',
          ],
        },
        {
          id: 5,
          category: 'امنیت',
          question: 'داده‌های مشتریان من با اسلاتیفای چقدر امن است؟',
          answer: `امنیت اولویت اصلی ما است:\n\n**حفاظت از داده‌ها:**\n• رمزگذاری انتها به انتها برای همه ارتباطات\n• مطابق با GDPR و CCPA\n• گواهی SOC 2 Type II\n• ممیزی‌های امنیتی منظم و تست نفوذ\n\n**زیرساخت:**\n• میزبانی ابری AWS با 99.9% زمان فعالیت\n• پشتیبان‌گیری خودکار هر 6 ساعت\n• پروتکل‌های بازیابی فاجعه\n• افزونگی داده‌های جغرافیایی\n\n**کنترل دسترسی:**\n• احراز هویت چندعاملی\n• مجوزهای مبتنی بر نقش\n• گزارش‌های ممیزی برای همه فعالیت‌ها\n• نقاط پایانی API امن\n\n**انطباق:**\n• مطابق با HIPAA برای کسب‌وکارهای بهداشتی\n• مطابق با PCI DSS برای پردازش پرداخت`,
          tags: ['امنیت', 'حفاظت داده', 'رمزگذاری', 'انطباق', 'gdpr', 'hipaa'],
        },
        {
          id: 6,
          category: 'پشتیبانی',
          question: 'چه نوع پشتیبانی ارائه می‌دهید؟',
          answer: `ما پشتیبانی جامع در همه طرح‌ها ارائه می‌دهیم:\n\n**کانال‌های پشتیبانی:**\n• چت زنده (24/7 برای طرح‌های پیشرفته+)\n• پشتیبانی ایمیل با زمان پاسخ کمتر از 4 ساعت\n• پشتیبانی تلفنی برای طرح‌های استاندارد+\n• کمک تماس ویدیویی برای مسائل پیچیده\n\n**منابع:**\n• پایگاه دانش جامع\n• آموزش‌های ویدیویی و وبینارها\n• راهنماهای بهترین شیوه‌ها\n• انجمن جامعه\n\n**آموزش:**\n• متخصص آموزش اختصاصی\n• کمک راه‌اندازی زنده\n• جلسات آموزش کارکنان\n• طراحی گردش کار سفارشی\n\n**پشتیبانی مداوم:**\n• بررسی‌های منظم برای بهینه‌سازی\n• آموزش به‌روزرسانی ویژگی‌ها\n• نظارت عملکرد و هشدارها`,
          tags: ['پشتیبانی', 'کمک', 'آموزش', 'راه‌اندازی', 'کمک'],
        },
        {
          id: 7,
          category: 'فنی',
          question: 'آیا برای استفاده از اسلاتیفای به دانش فنی نیاز دارم؟',
          answer: `اصلاً! اسلاتیفای برای کاربران غیرفنی طراحی شده است:\n\n**طراحی کاربرپسند:**\n• داشبورد بصری با رابط کشیدن و رها کردن\n• جادوگرهای راه‌اندازی گام به گام\n• سازندگان گردش کار بصری\n• نیازی به کدنویسی نیست\n\n**یکپارچگی آسان:**\n• اتصالات یک کلیکی شبکه‌های اجتماعی\n• همگام‌سازی خودکار تقویم\n• قالب‌های از پیش ساخته برای سناریوهای رایج\n• پیش‌فرض‌های هوشمند که از جعبه کار می‌کنند\n\n**پشتیبانی موجود:**\n• کمک راه‌اندازی زنده\n• اشتراک‌گذاری صفحه برای پیکربندی‌های پیچیده\n• آموزش‌های ویدیویی برای هر ویژگی\n• مدیر موفقیت مشتری اختصاصی\n\n**گزینه‌های پیشرفته:**\n• دسترسی API برای کاربران فنی\n• یکپارچگی‌های سفارشی موجود\n• پشتیبانی وب‌هوک برای توسعه‌دهندگان`,
          tags: ['فنی', 'آسان', 'کاربرپسند', 'بدون کدنویسی', 'راه‌اندازی'],
        },
        {
          id: 8,
          category: 'تأثیر کسب‌وکار',
          question: 'در ماه اول چه نتایجی می‌توانم انتظار داشته باشم؟',
          answer: `اکثر کسب‌وکارها بهبودهای فوری می‌بینند:\n\n**هفته 1:**\n• 50-70% کاهش تماس‌های تلفنی مربوط به رزرو\n• پاسخ‌های خودکار به سوالات رایج\n• برنامه‌ریزی قرارملاقات پایه کار می‌کند\n\n**هفته 2-3:**\n• 60-80% کاهش غیبت‌ها (با یادآوری‌های فعال)\n• کارکنان 40-60% صرفه‌جویی زمان گزارش می‌دهند\n• بهبود رضایت مشتری\n\n**نتایج ماه 1:**\n• 85%+ کاهش غیبت (متوسط صنعت)\n• 15-25 ساعت/هفته صرفه‌جویی زمان کارکنان\n• 20-35% افزایش تبدیل رزرو\n• 95%+ رضایت مشتری از فرآیند رزرو\n\n**مزایای بلندمدت:**\n• 30-50% افزایش درآمد از کاهش غیبت‌ها\n• بهبود روحیه و بهره‌وری کارکنان\n• حفظ بهتر مشتری و ارجاعات\n• رشد مقیاس‌پذیر بدون افزایش متناسب کارکنان`,
          tags: [
            'نتایج',
            'بازگشت سرمایه',
            'مزایا',
            'جدول زمانی',
            'بهبودها',
            'غیبت‌ها',
          ],
        },
      ],
    },
  };

  const currentContent = content?.[language];

  useEffect(() => {
    const filtered = currentContent?.faqs?.filter(
      (faq) =>
        faq?.question?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        faq?.answer?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        faq?.tags?.some((tag) =>
          tag?.toLowerCase()?.includes(searchTerm?.toLowerCase())
        )
    );
    setFilteredFAQs(filtered);
  }, [searchTerm, currentContent?.faqs]);

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <section ref={sectionRef} className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div
          className={`text-center mb-16 ${language === 'fa' ? 'rtl' : 'ltr'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {currentContent?.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {currentContent?.subtitle}
          </p>

          {/* Search */}
          <div className="max-w-md mx-auto">
            <Input
              type="search"
              placeholder={currentContent?.searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="text-center"
            />
          </div>
        </div>

        {/* FAQ List */}
        <div className="max-w-4xl mx-auto">
          {filteredFAQs?.length > 0 ? (
            <div className="space-y-4">
              {filteredFAQs?.map((faq, index) => (
                <div
                  key={faq?.id}
                  ref={(el) => (faqsRef.current[index] = el)}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <button
                    onClick={() => toggleFAQ(faq?.id)}
                    className={`w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200 ${
                      language === 'fa' ? 'text-right' : 'text-left'
                    }`}>
                    <div className="flex-1">
                      <div className="text-sm text-blue-600 font-medium mb-1">
                        {faq?.category}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {faq?.question}
                      </h3>
                    </div>
                    <Icon
                      name={openFAQ === faq?.id ? 'ChevronUp' : 'ChevronDown'}
                      size={20}
                      color="#6B7280"
                      className={`transition-transform duration-200 ${
                        language === 'fa' ? 'ml-4' : 'mr-4'
                      }`}
                    />
                  </button>

                  {openFAQ === faq?.id && (
                    <div className="px-6 pb-6">
                      <div className="pt-4 border-t border-gray-100">
                        <div
                          className={`text-gray-700 leading-relaxed whitespace-pre-line ${
                            language === 'fa' ? 'text-right' : 'text-left'
                          }`}>
                          {faq?.answer}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Icon
                name="Search"
                size={48}
                color="#9CA3AF"
                className="mx-auto mb-4"
              />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                {currentContent?.noResults}
              </h3>
              <p className="text-gray-500">
                {language === 'en'
                  ? 'Try different keywords or browse all questions above.'
                  : 'کلمات کلیدی مختلف امتحان کنید یا همه سوالات بالا را مرور کنید.'}
              </p>
            </div>
          )}
        </div>

        {/* Contact Support */}
        <div className="text-center mt-16">
          <div className="bg-white rounded-2xl p-8 shadow-lg max-w-2xl mx-auto">
            <Icon
              name="MessageCircle"
              size={48}
              color="#3B82F6"
              className="mx-auto mb-4"
            />
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {language === 'en' ? 'Still have questions?' : 'هنوز سوال دارید؟'}
            </h3>
            <p className="text-gray-600 mb-6">
              {language === 'en'
                ? 'Our support team is here to help you get the most out of Slotyfi.'
                : 'تیم پشتیبانی ما اینجا است تا به شما کمک کند بیشترین استفاده را از اسلاتیفای ببرید.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                className="bg-blue-600 hover:bg-blue-700"
                iconName="MessageSquare"
                iconPosition="left">
                {language === 'en' ? 'Live Chat' : 'چت زنده'}
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Mail"
                iconPosition="left">
                {language === 'en' ? 'Email Support' : 'پشتیبانی ایمیل'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
